import { DocumentIcon } from "@heroicons/react/24/outline";
import { Badge } from "@mantine/core";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import numeral from "numeral";
import { sendOfferParticipationMessage } from "graphql/mutations/publish-offer";
import { holdKavaDecision } from "graphql/mutations/holdOffers";
import HoldOfferDocumentViewer from "./components/HoldOfferDocumentViewer";
import OfferThreadContainer from "../AllBusiness/components/OfferThreadContainer";

const HoldOfferActions: FC<{ offer: any; refresh?: () => void }> = ({ offer, refresh }) => {
    const { t } = useTranslation();
    const [threadOpen, setThreadOpen] = useState(false);
    const [docsOpen, setDocsOpen] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const offerId = offer?.offer_id || offer?.id;
    const participationId = offer?.offer_published?.reinsurer_participation?.offer_published_participant_id || undefined;
    const unreadThreads = (offer?.unread_transaction_threads ?? []).filter((m: any) => m !== null);
    const unreadMessageIds = unreadThreads.map((m: any) => m?.id).filter(Boolean) as string[];
    const hasDecision = offer?.hold_kava_participant?.decision;
    const businessName = offer?.classofbusiness?.business_name || offer?.class_of_business?.business_name;

    const [sendMessage] = useMutation(sendOfferParticipationMessage);
    const [makeDecision] = useMutation(holdKavaDecision, {
        onCompleted: () => { setIsProcessing(false); if (refresh) refresh(); },
        onError: (e) => { setIsProcessing(false); alert(`Error: ${e.message}`); },
    });

    const offerPublished = {
        offer_published_id: offer?.offer_published?.offer_published_id,
        published_by: "PlaceIT",
        offer: { offer_id: offerId, id: offerId, offer_detail: offer?.offer_detail, facultative_offer: offer?.facultative_offer, classofbusiness: { business_name: businessName }, class_of_business: { business_name: businessName } },
    } as any;

    const handleSendMessage = async (message: string) => sendMessage({ variables: { input: { offer_published_participant: participationId, offer_published_id: offer?.offer_published?.offer_published_id, policy_number: offer?.offer_detail?.policy_number || "", message, published_by: "PlaceIT" } } });
    const handleDecision = async (decision: "ACCEPTED" | "DECLINED") => { if (isProcessing || hasDecision) return; setIsProcessing(true); await makeDecision({ variables: { input: { offer_id: parseInt(offer.offer_detail.offersoffer_id), decision, published_by: offer.offer_from || "PLACEIT" } } }); };

    return (
        <div className="flex items-center gap-1.5 flex-nowrap">
            {!hasDecision && (
                <>
                    <button onClick={() => handleDecision("ACCEPTED")} disabled={isProcessing} className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium disabled:opacity-50">
                        {isProcessing ? "..." : t("columns.hold_offers.accept")}
                    </button>
                    <button onClick={() => handleDecision("DECLINED")} disabled={isProcessing} className="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium disabled:opacity-50">
                        {isProcessing ? "..." : t("columns.hold_offers.decline")}
                    </button>
                </>
            )}
            <button onClick={() => setThreadOpen(true)} className="relative inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200">
                <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                {t("pages.all_business.action_buttons.thread")}
                {unreadThreads.length > 0 && <span className="absolute -top-1 -right-1 flex items-center justify-center w-3.5 h-3.5 text-xs font-bold text-white bg-orange-500 rounded-full">{unreadThreads.length}</span>}
            </button>
            <button onClick={() => setDocsOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50">
                <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                {t("pages.all_business.action_buttons.docs")}
            </button>
            <OfferThreadContainer open={threadOpen} setOpen={setThreadOpen} offerPublished={offerPublished} participationId={participationId} unreadMessageIds={unreadMessageIds} onSendMessage={handleSendMessage} />
            <HoldOfferDocumentViewer open={docsOpen} setOpen={setDocsOpen} offer={offer} />
        </div>
    );
};

export const useHoldOfferColumns = (refresh?: () => void) => {
    const { t } = useTranslation();

    return useMemo(() => {
        const columns = [
            {
                accessor: "",
                name: t("columns.hold_offers.policy_number_insured"),
                render: (offer: any) => (
                    <div className="flex items-center max-w-md">
                        <div className="h-10 w-10 flex-shrink-0">
                            <DocumentIcon className="h-10 w-10 rounded-full" />
                        </div>
                        <div className="ml-4 break-all pl-3 truncate text-ellipsis">
                            <div className="font-medium truncate text-gray-900">
                                {offer?.offer_detail?.insured_by || "N/A"}
                            </div>
                            <div className="text-gray-500">
                                {offer?.offer_detail?.policy_number || "N/A"}
                            </div>
                        </div>
                    </div>
                ),
            },
            {
                accessor: "",
                name: t("columns.hold_offers.business_name"),
                render: (offer: any) => {
                    const isPlaceItOffer =
                        offer?.offer_from?.toLowerCase() === "placeit";
                    const businessName = !isPlaceItOffer
                        ? offer?.class_of_business?.business_name
                        : offer?.classofbusiness?.business_name;
                    return (
                        <div className="flex items-center">
                            <div className="font-medium text-gray-900">
                                {businessName || "N/A"}
                            </div>
                        </div>
                    );
                },
            },
            {
                accessor: "",
                name: t("columns.hold_offers.policy_period"),
                render: (offer: any) => (
                    <div className="flex items-center">
                        <div>
                            <div className="font-medium text-green-900">
                                {offer?.offer_detail?.period_of_insurance_from
                                    ? new Date(
                                          offer.offer_detail
                                              .period_of_insurance_from,
                                      ).toLocaleDateString("en-GB", {
                                          day: "2-digit",
                                          month: "2-digit",
                                          year: "numeric",
                                      })
                                    : "N/A"}
                            </div>
                            <div className="text-red-500">
                                {offer?.offer_detail?.period_of_insurance_to
                                    ? new Date(
                                          offer.offer_detail
                                              .period_of_insurance_to,
                                      ).toLocaleDateString("en-GB", {
                                          day: "2-digit",
                                          month: "2-digit",
                                          year: "numeric",
                                      })
                                    : "N/A"}
                            </div>
                        </div>
                    </div>
                ),
            },
            {
                accessor: "",
                name: t("columns.hold_offers.premium_sum_insured"),
                render: (offer: any) => (
                    <div className="flex items-center max-w-md">
                        <div className="break-all pl-3 truncate text-ellipsis">
                            <div className="font-medium truncate text-gray-900">
                                {offer?.offer_detail?.currency}{" "}
                                {offer?.premium
                                    ? numeral(offer.premium).format("#,#.##")
                                    : "N/A"}
                            </div>
                            <div className="text-gray-500">
                                {offer?.sum_insured
                                    ? numeral(offer.sum_insured).format(
                                          "#,#.##",
                                      )
                                    : "N/A"}
                            </div>
                        </div>
                    </div>
                ),
            },
            {
                accessor: "",
                name: t("columns.hold_offers.fac_share"),
                render: (offer: any) => (
                    <div className="flex items-center">
                        <div className="font-medium text-gray-900">
                            {offer?.facultative_offer
                                ? `${offer.facultative_offer}%`
                                : "N/A"}
                        </div>
                    </div>
                ),
            },
            {
                accessor: "",
                name: t("columns.hold_offers.status"),
                render: (offer: any) => {
                    const decision = offer?.hold_kava_participant?.decision;

                    if (!decision) {
                        return (
                            <Badge color="yellow">
                                {t("columns.hold_offers.pending")}
                            </Badge>
                        );
                    }

                    const isAccepted = decision?.toUpperCase() === "ACCEPTED";
                    const isDeclined = decision?.toUpperCase() === "DECLINED";

                    return (
                        <Badge
                            color={
                                isAccepted
                                    ? "green"
                                    : isDeclined
                                      ? "red"
                                      : "gray"
                            }>
                            {isAccepted
                                ? t("columns.hold_offers.accepted")
                                : isDeclined
                                  ? t("columns.hold_offers.declined")
                                  : t("columns.hold_offers.unknown")}
                        </Badge>
                    );
                },
            },
            {
                accessor: "",
                name: t("columns.hold_offers.countdown"),
                render: (offer: any) => {
                    const periodTo = offer?.offer_detail?.period_of_insurance_to;
                    if (!periodTo)
                        return <span className="text-gray-500">N/A</span>;

                    const expiryDate = new Date(periodTo);
                    const today = new Date();
                    const msPerDay = 1000 * 60 * 60 * 24;
                    const daysSinceExpiry = Math.floor(
                        (today.getTime() - expiryDate.getTime()) / msPerDay,
                    );

                    // Policy has not yet expired — countdown hasn't started
                    if (daysSinceExpiry < 0) {
                        return (
                            <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-50">
                                <span className="font-medium text-sm text-green-700">
                                    {t("columns.hold_offers.not_expired")}
                                </span>
                            </div>
                        );
                    }

                    // Past the 15-day grace window
                    if (daysSinceExpiry > 15) {
                        return (
                            <div className="inline-flex items-center px-3 py-1 rounded-full bg-red-50">
                                <span className="font-medium text-sm text-red-700">
                                    {t("columns.hold_offers.expired")}
                                </span>
                            </div>
                        );
                    }

                    // Within the 15-day grace window
                    const graceDaysLeft = 15 - daysSinceExpiry;
                    const textColor = graceDaysLeft <= 3 ? "text-red-700" : "text-orange-700";
                    const bgColor = graceDaysLeft <= 3 ? "bg-red-50" : "bg-orange-50";

                    return (
                        <div
                            className={`inline-flex items-center px-3 py-1 rounded-full ${bgColor}`}>
                            <span className={`font-medium text-sm ${textColor}`}>
                                {graceDaysLeft === 1
                                    ? t("columns.hold_offers.grace_day_left")
                                    : t("columns.hold_offers.grace_days_left", {
                                          days: graceDaysLeft,
                                      })}
                            </span>
                        </div>
                    );
                },
            },

            {
                accessor: "",
                name: t("columns.hold_offers.action"),
                render: (offer: any) => (
                    <HoldOfferActions offer={offer} refresh={refresh} />
                ),
            },
        ];

        return columns;
    }, [t, refresh]);
};
